import { Space } from '@sravni/react-design-system';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Text } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';
import cn from 'classnames';
import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useConfirmationSubmitDeliveryMutation } from 'entities/preApprovals';

import { SmsCodeField } from './fields';
import { hasSmsConfirmationError } from './helpers';
import { CONFIRMATION_FORM_FIELDS } from './PreApprovalConfirmationForm.config';
import styles from './PreApprovalConfirmationForm.module.scss';
import {
  CONFIRMATION_FORM_TEXTS,
  SMS_CODE_ERROR_TEXT,
  TECHNICAL_ERROR_TEXT,
  TOO_MANY_REQUEST_ERROR_TEXT,
} from './PreApprovalConfirmationForm.texts';
import type { ConfirmationFormValues } from './schema';
import type { PreApprovalConfirmationFormProps } from './types';
import { useAutoSubmitSmsCodeOnDesktop } from './useAutoSubmitSmsCodeOnDesktop';
import { CONFIRMATION_STATUS_CODES, useSendSmsConfirmation } from './useSendSmsConfirmation';

export const PreApprovalConfirmationFormInner: FC<PreApprovalConfirmationFormProps> = (props) => {
  const {
    onFormSubmit,
    formId,
    isDisabledByTooManyRequestsError = false,
    onDisableByTooManyRequests,
    offerData,
    onSmsCodeEvent,
  } = props;
  const { preApprovalOfferId = '' } = offerData;
  const { handleSubmit, setError, setValue, formState, clearErrors } = useFormContext<ConfirmationFormValues>();
  const { isSubmitting } = formState;
  const isMobile = useIsMobile();
  const [isTooManyRequestsError, setIsTooManyRequestsError] = useState(isDisabledByTooManyRequestsError);
  const [confirmationSubmitDelivery] = useConfirmationSubmitDeliveryMutation();

  const {
    isSmsSending,
    sendSmsAgain,
    retryTimerText,
    secondsLeft,
    statusCode: smsConfirmationStatusCode,
    isTooLongRetryTime,
  } = useSendSmsConfirmation(preApprovalOfferId);

  useEffect(() => {
    if (isTooManyRequestsError || isTooLongRetryTime) {
      setError(CONFIRMATION_FORM_FIELDS.SMS_CODE, {
        type: 'custom',
        message: TOO_MANY_REQUEST_ERROR_TEXT,
      });
      setValue(CONFIRMATION_FORM_FIELDS.SMS_CODE, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTooManyRequestsError, isTooLongRetryTime]);

  useEffect(() => {
    if (!smsConfirmationStatusCode) {
      return;
    }

    switch (smsConfirmationStatusCode) {
      case CONFIRMATION_STATUS_CODES.TOO_MANY_REQUESTS:
      case CONFIRMATION_STATUS_CODES.CREATED:
      case CONFIRMATION_STATUS_CODES.SUCCESS: {
        break;
      }
      case CONFIRMATION_STATUS_CODES.SEND_DOUBLE_SMS: {
        onFormSubmit?.();
        break;
      }
      default: {
        setError(CONFIRMATION_FORM_FIELDS.SMS_CODE, {
          type: 'custom',
          message: TECHNICAL_ERROR_TEXT,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsConfirmationStatusCode]);

  const onSubmit = useCallback(
    async (values: ConfirmationFormValues) => {
      const smsCode = values[CONFIRMATION_FORM_FIELDS.SMS_CODE];

      if (!smsCode) {
        return;
      }

      const result = await confirmationSubmitDelivery({
        offerId: preApprovalOfferId,
        code: smsCode,
      }).unwrap();

      if (result.verified) {
        onSmsCodeEvent?.(offerData, 'verified');
        onFormSubmit?.();
        return;
      }

      onSmsCodeEvent?.(offerData, 'failed');

      switch (result.statusCode) {
        case CONFIRMATION_STATUS_CODES.SEND_DOUBLE_SMS: {
          onFormSubmit?.();
          break;
        }
        case CONFIRMATION_STATUS_CODES.TOO_MANY_REQUESTS: {
          onSmsCodeEvent?.(offerData, 'tooManyRequests');
          setIsTooManyRequestsError(true);
          onDisableByTooManyRequests?.();
          break;
        }
        case CONFIRMATION_STATUS_CODES.PAYLOAD_ERROR: {
          setError(CONFIRMATION_FORM_FIELDS.SMS_CODE, {
            type: 'custom',
            message: SMS_CODE_ERROR_TEXT,
          });
          setValue(CONFIRMATION_FORM_FIELDS.SMS_CODE, '');
          break;
        }
        case CONFIRMATION_STATUS_CODES.SUCCESS: {
          setError(CONFIRMATION_FORM_FIELDS.SMS_CODE, {
            type: 'custom',
            message: !result.verified ? SMS_CODE_ERROR_TEXT : TECHNICAL_ERROR_TEXT,
          });
          setValue(CONFIRMATION_FORM_FIELDS.SMS_CODE, '');
          break;
        }
        default: {
          setError(CONFIRMATION_FORM_FIELDS.SMS_CODE, {
            type: 'custom',
            message: TECHNICAL_ERROR_TEXT,
          });
          setValue(CONFIRMATION_FORM_FIELDS.SMS_CODE, '');
        }
      }
    },
    [
      confirmationSubmitDelivery,
      preApprovalOfferId,
      onSmsCodeEvent,
      offerData,
      onFormSubmit,
      onDisableByTooManyRequests,
      setError,
      setValue,
    ],
  );

  const onCLickSendAgain = useCallback(() => {
    onSmsCodeEvent?.(offerData, 'requestNewCode');
    sendSmsAgain();
    setValue(CONFIRMATION_FORM_FIELDS.SMS_CODE, '');
    clearErrors(CONFIRMATION_FORM_FIELDS.SMS_CODE);
  }, [sendSmsAgain, setValue, clearErrors, onSmsCodeEvent, offerData]);

  const isSmsError = hasSmsConfirmationError(smsConfirmationStatusCode);

  const isFormDisabled = isSmsSending || isSmsError || isSubmitting || isTooManyRequestsError || isTooLongRetryTime;
  const isAvailableToSendAgain = secondsLeft === 0;
  const isShowLoader = isSmsSending || isSubmitting;

  useAutoSubmitSmsCodeOnDesktop({
    isFormDisabled,
    submit: () => handleSubmit(onSubmit)(),
  });

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Space
        direction="vertical"
        size={2}
      >
        <SmsCodeField
          name={CONFIRMATION_FORM_FIELDS.SMS_CODE}
          isDisabled={isFormDisabled}
          isLoading={!isMobile && isShowLoader}
        />
        {!isFormDisabled &&
          (isAvailableToSendAgain ? (
            <Button
              variant="text"
              color="blue"
              onClick={onCLickSendAgain}
              className={styles.retryButton}
            >
              {CONFIRMATION_FORM_TEXTS.RETRY_BUTTON_LABEL}
            </Button>
          ) : (
            <Text className={cn('h-color-D60', styles.retryTimerText)}>
              {`${CONFIRMATION_FORM_TEXTS.RETRY_AVAILABLE_AFTER_LABEL} ${retryTimerText}`}
            </Text>
          ))}
        {isMobile && (
          <Button
            variant="primary"
            size={52}
            type="submit"
            block
            disabled={isFormDisabled}
            loading={isShowLoader}
          >
            {CONFIRMATION_FORM_TEXTS.SUBMIT_BUTTON_LABEL}
          </Button>
        )}
      </Space>
    </form>
  );
};
